/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  ONE DEFINITION OF "REVENUE"
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * This platform already has several revenue calculations that do not
 * reconcile — the payout statement, the Transactions page and the Dashboard
 * each answer the question slightly differently, for reasons that were
 * deliberate at the time.
 *
 * The brand dashboard must not become another one. It shows the same figure a
 * studio owner sees on their own dashboard, summed across their locations, so
 * the two agree **by construction**: same endpoint, same derivation, called
 * once per location. If the studio dashboard is ever corrected, the brand
 * total follows without anyone remembering to change it here.
 *
 * Extracted verbatim from RevenueStatCardWithHoverBreakdown, which was the
 * only place it existed.
 */

export interface TransactionStatsLike {
  grandTotal?: { netRevenue?: number; tax?: number; count?: number; captureCount?: number } | null;
  cashManual?: { total?: number; tax?: number; count?: number } | null;
}

export interface DerivedRevenue {
  /** Razorpay take, net of recorded GST. */
  razorpayNet: number;
  razorpayTax: number;
  razorpayGross: number;
  razorpayCount: number;

  /** Cash / manual take, net of recorded GST. */
  cashNet: number;
  cashTax: number;
  cashGross: number;
  cashCount: number;

  /** Razorpay + cash, net of GST — the headline number. */
  netRevenue: number;
  tax: number;
  /** net + tax. What actually changed hands. */
  total: number;
}

/**
 * Both buckets are already filtered upstream to exclude cancelled bookings,
 * refunds, free entries and pack REDEMPTIONS (`modeOfPayment: subscription`) —
 * see `getTransactionStats`. A pack *purchase* is a real payment and counts; a
 * later class-by-class redemption of it does not, or the same money would be
 * counted twice.
 */
export function deriveRevenue(stats?: TransactionStatsLike | null): DerivedRevenue {
  const razorpayNet = stats?.grandTotal?.netRevenue ?? 0;
  const razorpayTax = stats?.grandTotal?.tax ?? 0;
  const cashGross = stats?.cashManual?.total ?? 0;
  const cashTax = stats?.cashManual?.tax ?? 0;
  const cashNet = cashGross - cashTax;

  // `grandTotal.count` mixes in cash counts — a long-standing convention of
  // that endpoint — so the pure Razorpay count is what remains after removing
  // the Cash/Manual card's own (already accurate) count.
  const cashCount = stats?.cashManual?.count ?? 0;
  // captureCount is the exact number of Razorpay payments; the subtraction is
  // the fallback for a backend that predates it.
  const razorpayCount =
    stats?.grandTotal?.captureCount ??
    Math.max(0, (stats?.grandTotal?.count ?? 0) - cashCount);

  const netRevenue = razorpayNet + cashNet;
  const tax = razorpayTax + cashTax;

  return {
    razorpayNet,
    razorpayTax,
    razorpayGross: razorpayNet + razorpayTax,
    razorpayCount,
    cashNet,
    cashTax,
    cashGross,
    cashCount,
    netRevenue,
    tax,
    total: netRevenue + tax,
  };
}

/** Sum several locations into one brand figure. Addition, nothing clever. */
export function sumRevenue(parts: DerivedRevenue[]): DerivedRevenue {
  return parts.reduce<DerivedRevenue>(
    (a, b) => ({
      razorpayNet: a.razorpayNet + b.razorpayNet,
      razorpayTax: a.razorpayTax + b.razorpayTax,
      razorpayGross: a.razorpayGross + b.razorpayGross,
      razorpayCount: a.razorpayCount + b.razorpayCount,
      cashNet: a.cashNet + b.cashNet,
      cashTax: a.cashTax + b.cashTax,
      cashGross: a.cashGross + b.cashGross,
      cashCount: a.cashCount + b.cashCount,
      netRevenue: a.netRevenue + b.netRevenue,
      tax: a.tax + b.tax,
      total: a.total + b.total,
    }),
    deriveRevenue(null),
  );
}
